import { cn } from "@/lib/utils/cn";

/**
 * StarRating — renders a 5-star rating with optional half-star precision.
 *
 * Score must be on a 1-5 scale. Rendering rules:
 *   - i <= floor(score) → full gold star
 *   - i === floor(score) + 1 && decimal >= 0.3 → half-filled gold star
 *   - otherwise → empty gray star
 *
 * So 3.7 renders as ★ ★ ★ ½ ☆ (3 full + 1 half + 1 empty).
 */
export function StarRating({
  score,
  size = "text-base",
}: {
  score: number;
  size?: string;
}) {
  const clamped = Math.max(0, Math.min(5, score || 0));
  const full = Math.floor(clamped);
  const remainder = clamped - full;
  const hasHalf = remainder >= 0.3 && full < 5;

  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        if (i <= full) {
          return (
            <span key={i} className={cn(size, "text-gold")}>
              ★
            </span>
          );
        }
        if (i === full + 1 && hasHalf) {
          return (
            <span
              key={i}
              className={cn(size, "relative inline-block text-surface-light")}
            >
              ★
              <span
                className="absolute inset-0 text-gold overflow-hidden pointer-events-none"
                style={{ width: "50%" }}
              >
                ★
              </span>
            </span>
          );
        }
        return (
          <span key={i} className={cn(size, "text-surface-light")}>
            ★
          </span>
        );
      })}
    </span>
  );
}
